import { useCallback, useEffect, useRef, useState } from "react";
import Header from "./Header";
import { CoreContainer, Notification } from "./style";
import {
  Button,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  Spinner,
  Table,
} from "reactstrap";
import { Option } from "../product/style";
import {
  getLaminates,
  updateLaminate,
  publishLaminate,
  approveLaminate,
} from "../project/service";
import { debounce } from "lodash";
import NoImage from "../assets/img/no_image.png";
import Select from "react-select";
import { LAM_CATEGORIES, LAM_BRANDS, LAM_THICKNESSES } from "../constants";

const Laminates = () => {
  const [currentTab, setCurrentTab] = useState("uncategorized");
  const [laminates, setLaminates] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [openForm, setOpenForm] = useState(false);
  const [formDetails, setFormDetails] = useState({});
  const [lamLoading, setLamLoading] = useState(false);
  const uploadFileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [notification, setNotification] = useState({ type: "", message: "" });
  const [isNotificationVisible, setIsNotificationVisible] = useState(false);

  const toggleForm = () => {
    setOpenForm(!openForm);
    setFormDetails({});
    removeFile();
  };

  const showForm = (laminate) => {
    setOpenForm(true);
    setFormDetails({
      id: laminate.id,
      name: laminate.name,
      brand: laminate.brand,
      range: laminate.range,
      code: laminate.code,
      product_name: laminate.product_name,
      finish: laminate.finish,
      thickness: laminate.thickness,
      category: laminate.category,
      rotatable: laminate.rotatable,
    });

    removeFile();
  };

  const handleSearch = (searchValue) => {
    setSearchValue(searchValue);
    debounceSearch(searchValue);
  };

  const debouncedSearch = (value) => setSearchTerm(value);

  const debounceSearch = useCallback(debounce(debouncedSearch, 500), []);

  useEffect(() => {
    if (currentTab == "all" && !searchTerm.trim()) {
      setLaminates([]);
      return;
    }

    loadLaminates();
  }, [currentTab, searchTerm]);

  const loadLaminates = () => {
    setLamLoading(true);

    getLaminates({
      searchTerm: currentTab == "all" ? searchTerm : "",
      manage: true,
      status: currentTab,
    })
      .then((response) => {
        setLaminates(response);
      })
      .catch((error) => {
        setIsNotificationVisible(true);
        setNotification({ message: error.error, type: "error" });
      })
      .finally(() => setLamLoading(false));
  };

  const saveLaminate = (details, image) => {
    updateLaminate({ image, ...details })
      .then(({ laminate }) => {
        toggleForm();
        loadLaminates();
        setIsNotificationVisible(true);
        setNotification({ message: "Laminates updated", type: "success" });
      })
      .catch(({ error }) => {
        setIsNotificationVisible(true);
        setNotification({ message: error, type: "error" });
      });
  };

  const publish = (laminate) => {
    if (confirm("Publish this laminate for all customers?")) {
      publishLaminate(laminate.id)
        .then(() => {
          loadLaminates();
          setIsNotificationVisible(true);
          setNotification({ message: "Laminates published", type: "success" });
        })
        .catch((error) => {
          setIsNotificationVisible(true);
          setNotification({ message: error.error, type: "error" });
        });
    }
  };

  const categories = LAM_CATEGORIES.map((e) => ({
    label: e,
    value: e,
  }));

  const brands = LAM_BRANDS.map((e) => ({
    label: e,
    value: e,
  }));

  const thicknesses = LAM_THICKNESSES.map((e) => ({
    label: e,
    value: e,
  }));

  const handleFileChange = (event) => {
    const file = event.target.files[0];

    if (file) {
      const fileType = file.type;
      const validImageTypes = ["image/jpeg", "image/png"];

      if (!validImageTypes.includes(fileType)) {
        setErrorMessage("Please select a valid image file.");
        setSelectedFile(null);
        event.target.value = "";
      } else {
        setErrorMessage("");
        setSelectedFile(file);
      }
    }
  };

  const removeFile = () => {
    if (uploadFileInputRef.current) uploadFileInputRef.current.value = "";
    setSelectedFile(null);
  };

  const approvePrice = (laminate) => {
    if (confirm(`Do you need to approve "${laminate.name}" laminate?`)) {
      approveLaminate(laminate.id)
        .then(() => {
          loadLaminates();
          setIsNotificationVisible(true);
          setNotification({ message: "Laminate approved", type: "success" });
        })
        .catch((error) => {
          setIsNotificationVisible(true);
          setNotification({ message: error.error, type: "error" });
        });
    }
  };

  return (
    <>
      <Header nav={true} />
      {isNotificationVisible && (
        <Notification
          color={notification.type === "error" ? "danger" : "success"}
          isOpen={isNotificationVisible}
          toggle={() => setIsNotificationVisible(false)}
        >
          {notification.message}
        </Notification>
      )}
      <div className="px-5 py-4">
        <div className="btn-group mb-4" style={{ height: "40px" }}>
          <Option
            onClick={() => setCurrentTab("private")}
            className={`default-option ${
              currentTab == "private" && "selected-option"
            }`}
          >
            Private
          </Option>
          <Option
            onClick={() => setCurrentTab("uncategorized")}
            className={`default-option ${
              currentTab == "uncategorized" && "selected-option"
            }`}
          >
            Uncategorized
          </Option>
          <Option
            onClick={() => setCurrentTab("all")}
            className={`default-option ${
              currentTab == "all" && "selected-option"
            }`}
          >
            All
          </Option>
        </div>
        {currentTab == "all" && (
          <Input
            type="search"
            value={searchValue}
            onChange={(e) => handleSearch(e.target.value)}
            className="rounded-1 px-3 py-2 mb-4"
            placeholder="Search laminate"
            autoFocus
          />
        )}
        <Table bordered className="">
          <thead>
            <tr>
              {currentTab == "private" && (
                <>
                  <th>Uploaded at</th>
                  <th>Org</th>
                </>
              )}
              <th className="text-center">Image</th>
              <th>Name</th>
              <th>Category</th>
              <th>Rotatable</th>
              <th>Action</th>
            </tr>
          </thead>
          {!lamLoading && (
            <tbody>
              {laminates.map((laminate, index) => (
                <tr key={index}>
                  {currentTab == "private" && (
                    <>
                      <td>{laminate.created_at}</td>
                      <td>{laminate.org}</td>
                    </>
                  )}
                  <td className="text-center">
                    <img
                      className="rounded-circle"
                      style={{ width: "30px", height: "30px" }}
                      src={laminate.image ? laminate.image : NoImage}
                    />
                  </td>
                  <td>{laminate.name}</td>
                  <td>{laminate.category}</td>
                  <td>{laminate.rotatable ? "Yes" : "No"}</td>
                  <td>
                    <a
                      href="#"
                      onClick={() => showForm(laminate)}
                      className="pointer"
                    >
                      Edit
                    </a>
                    {laminate.is_private && laminate.price_approved && (
                      <a
                        href="#"
                        onClick={() => publish(laminate)}
                        className="pointer ms-2"
                      >
                        Publish
                      </a>
                    )}
                    {!laminate.price_approved && (
                      <a
                        href="#"
                        onClick={() => approvePrice(laminate)}
                        className="pointer ms-2"
                      >
                        Approve Price
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          )}
        </Table>
        {lamLoading && (
          <CoreContainer height="20vh">
            <Spinner size="sm"></Spinner>
          </CoreContainer>
        )}
        {currentTab == "all" &&
          searchTerm &&
          laminates.length === 0 &&
          !lamLoading && (
            <p className="mt-5 text-center">No Laminates found...</p>
          )}
      </div>
      <Modal isOpen={openForm} toggle={toggleForm} backdrop="static">
        <ModalBody>
          <div className="mt-3">
            <Label className="m-0 fw-bold">Brand</Label>
            <Select
              className="mt-1"
              options={brands}
              value={brands.find((e) => e.value == formDetails.brand)}
              onChange={(e) =>
                setFormDetails({ ...formDetails, brand: e.value })
              }
            />
          </div>
          <div className="mt-3">
            <Label className="m-0 fw-bold">Range</Label>
            <Input
              id="name"
              type="text"
              value={formDetails.range}
              onChange={(e) =>
                setFormDetails({ ...formDetails, range: e.target.value })
              }
              className="mt-1 rounded-1 py-2"
            />
          </div>
          <div className="mt-3">
            <Label className="m-0 fw-bold">Code</Label>
            <Input
              id="name"
              type="text"
              value={formDetails.code}
              onChange={(e) =>
                setFormDetails({ ...formDetails, code: e.target.value })
              }
              className="mt-1 rounded-1 py-2"
            />
          </div>
          <div className="mt-3">
            <Label className="m-0 fw-bold">Name</Label>
            <Input
              id="name"
              type="text"
              value={formDetails.product_name}
              onChange={(e) =>
                setFormDetails({ ...formDetails, product_name: e.target.value })
              }
              className="mt-1 rounded-1 py-2"
            />
          </div>
          <div className="mt-3">
            <Label className="m-0 fw-bold">Finish</Label>
            <Input
              id="name"
              type="text"
              value={formDetails.finish}
              onChange={(e) =>
                setFormDetails({ ...formDetails, finish: e.target.value })
              }
              className="mt-1 rounded-1 py-2"
            />
          </div>
          <div className="mt-3">
            <Label className="m-0 fw-bold">Thickness</Label>
            <Select
              className="mt-1"
              options={thicknesses}
              value={thicknesses.find((e) => e.value == formDetails.thickness)}
              onChange={(e) =>
                setFormDetails({ ...formDetails, thickness: e.value })
              }
            />
          </div>
          <div className="mt-3">
            <Label className="m-0 fw-bold">Category</Label>
            <Select
              className="mt-1"
              options={categories}
              value={categories.find((e) => e.value == formDetails.category)}
              onChange={(e) =>
                setFormDetails({ ...formDetails, category: e.value })
              }
            />
          </div>
          <div className="mt-3">
            <Label className="m-0 fw-bold">Image</Label>
            <div className="mt-1 d-flex align-items-center">
              <Label
                className="border border-secondary rounded-2 m-0"
                htmlFor="file-upload"
                style={{ padding: "6px 14px" }}
              >
                Select File
              </Label>
              <Input
                className="d-none"
                id="file-upload"
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                innerRef={uploadFileInputRef}
              />

              {selectedFile && (
                <div
                  className="d-flex align-items-center ms-2 column-gap-1"
                  style={{ fontSize: "14px" }}
                >
                  <span>{selectedFile.name}</span>
                  <i
                    className="fa-regular fa-circle-xmark pointer danger-on-hover"
                    onClick={() => removeFile()}
                  />
                </div>
              )}

              {errorMessage && (
                <span className="ms-2 text-danger">{errorMessage}</span>
              )}
            </div>
          </div>
          <div className="mt-3 d-flex align-items-center">
            <Label for="rotatable" className="m-0 fw-bold">
              Rotatable
            </Label>
            <Input
              id="rotatable"
              type="checkbox"
              checked={formDetails.rotatable}
              onChange={() =>
                setFormDetails({
                  ...formDetails,
                  rotatable: !formDetails.rotatable,
                })
              }
              className="m-0 ms-2 rounded-1 form-check-input"
            />
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            color="primary"
            onClick={() => saveLaminate(formDetails, selectedFile)}
          >
            Submit
          </Button>
          <Button color="secondary" onClick={toggleForm}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default Laminates;
